import { Button, Card, Descriptions, Flex, Image, Input, Modal, Pagination, Spin, Switch, Tooltip, Typography } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { ColoredTag } from '../GenericCreateEdit/GenericElements';
import { PlusOutlined, RedoOutlined } from '@ant-design/icons';
import { validateUUID } from '../../utils';
import { useBadgeQuery } from '../../gql/hooks/useBadgeQueries';
import { useLazyQuery } from '@apollo/client';
import { flatten, formatGeneric } from '../../gql/dataParsing/searchItemFormatting';
import SEARCH_QUERY from '../../gql/queries/SEARCH_QUERY';
import ExpandableAlert from '../../../components/errors/ExpandableAlert';
import { DEFAULT_LOCALE_CODE } from '../../../const';

function formatBadgeFormItem( b ) {
    return {
        id: b.badgeId,
        image: b.image ?? b.badgeImage,
        name: b.defaultName,
        isPublished: b.isPublished,
        isRepeatable: b.isRepeatable,
        createdAt: b.createdAt,
        description: b.defaultDescription,
        personBadgeCount: b.personBadgeCount,
    };
}

export const BadgeSelectorFormItem = ( { value, name, onChange, disabled, publishedOnly = false, rewardEnabled = true } ) => {

    const DEFAULT_SORT = 'createdAt', DEFAULT_DIR = 'asc', SCORE_SORT = '_score', SCORE_DIR = 'desc';

    const [ search ] = useLazyQuery( SEARCH_QUERY );
    const [ fetchBadgeQuery ] = useBadgeQuery();

    const [ query, setQuery ] = useState();
    const [ page, setPage ] = useState( 1 );
    const [ sortBy, setSortBy ] = useState( DEFAULT_SORT );
    const [ sortDir, setSortDir ] = useState( DEFAULT_DIR );
    const [ isPublished, setIsPublished ] = useState( publishedOnly );

    const [ open, setOpen ] = useState( false );
    const [ selectedBadge, setSelectedBadge ] = useState();
    const [ badgeListLoading, setBadgeListLoading ] = useState( false );
    const [ modalError, setModalError ] = useState();

    const [ initialized, setInitialized ] = useState( false );
    const [ badgeLoading, setBadgeLoading ] = useState( false );
    
    const [ badge, setBadge ] = useState();
    const [ data, setData ] = useState();
    const [ totalCount, setTotalCount ] = useState();

    async function fetchBadges( { query: _query = query, page: _page = page, isPublished: _isPublished = isPublished, sortBy: _sortBy = sortBy, sortDir: _sortDir = sortDir } ) {
        setBadgeListLoading( true );
        setModalError();

        try {
            const variables = {
                types: [ 'badges' ],
                query: validateUUID( _query ) ? undefined : _query,
                _ids: validateUUID( _query ) ? [ _query ] : undefined,
                limit: 9,
                page: _page,
                isPublished: _isPublished ? _isPublished : undefined, //dont filter for Not Published if not set
                sort: _sortBy,
                order: _sortDir,
            };
            const response = await search( { variables } );
    
    
            const badgeData = response.data.search.edges.map( flatten ).map( formatGeneric ).map( formatBadgeFormItem );
            const total = response.data.search.totalCount;
    
            setData( badgeData );
            setTotalCount( total );
            setBadgeListLoading( false );
        } catch ( e ) {
            console.error( e );
            setModalError( <ExpandableAlert title='Could not fetch badges' error={ e } /> );
            setData( [] );
            setTotalCount();
            setBadgeListLoading( false );
        }
    }

    async function fetchBadge( id ) {
        try {
            const response = await fetchBadgeQuery( { id } );
    
            const badgeResult = formatBadgeFormItem( formatGeneric( flatten( response.data ) ) );
    
            setBadge( badgeResult );
            setSelectedBadge( badgeResult );
            setInitialized( true );
            setBadgeLoading( false );
        } catch ( e ) {
            console.error( e );
            // let it infinite load bc theres no recovering from this
        }
    }

    useEffect( () => {
        fetchBadges( {} );
    }, [] );

    useEffect( () => {
        if ( value && !initialized ) {
            setBadgeLoading( true );
            fetchBadge( value );
        }
    }, [ value ] );

    function remove() {
        if ( disabled ) {return;}
        onChange();
        setBadge();
    }

    function onSearch( value, e, { source } ) {

        const [ sort, dir ] = source === 'clear' ? [ DEFAULT_SORT, DEFAULT_DIR ] : [ SCORE_SORT, SCORE_DIR ];

        setQuery( value );
        setSortBy( sort );
        setSortDir( dir );
        setPage( 1 );
        fetchBadges( { query: value, page: 1, sortBy: sort, sortDir: dir } );
    }

    function onSwitch( checked ) {
        if ( publishedOnly ) {
            return;
        }
        setIsPublished( checked );
        setPage( 1 );
        fetchBadges( { isPublished: checked, page: 1 } );
    }

    function onPageChange( page ) {
        setPage( page );
        fetchBadges( { page } );
    }

    function onCancel() {
        setSelectedBadge( badge );
        setOpen( false );
    }

    function onOk() {
        setInitialized( true );
        setBadge( selectedBadge );
        onChange( selectedBadge.id );
        setOpen( false );
    }

    function onOpen() {
        setSelectedBadge( badge );
        setOpen( true );
    }

    const redoIcon = useMemo( () => <Tooltip title='Repeatable'>
        <RedoOutlined style={ {    
            width: 25,
            height: 25,
            backgroundColor: '#eb2f96',
            justifyContent: 'center',
            fontSize: 16,
            color: 'white',
            borderRadius: '50%'
        } } />
    </Tooltip>, [] );

    const displayData = useMemo( () => {
        if ( badgeListLoading ) {
            return new Array( 9 ).fill( null ).map( ( x, i ) => <Card key={ i } size='small' loading /> );
        }
        
        return data?.map( b => {
            const selected = selectedBadge && b.id === selectedBadge.id;
            return <BadgeItem 
                key={ b.id } 
                minimalInfo 
                badge={ b } 
                hoverable 
                onClick={ badge => setSelectedBadge( selected ? undefined : badge ) } 
                extra={ b.isRepeatable ? redoIcon : null } 
                style={ selected ? { border: '4px solid green' } : {} } />; 
        } );
    }, [ badgeListLoading, data, selectedBadge ] );


    return <div className='badge-selector'>
        { ( badge || badgeLoading ) ?
            <BadgeItem badge={ badge } extra={ <Button type='primary' onClick={ remove }>Reset</Button> } /> :
            <Button style={ { width: 100, height: 100 } } disabled={ !rewardEnabled || disabled } onClick={ onOpen } type='dashed' icon={ <PlusOutlined /> }>Add</Button>
        }
        <Modal
            className='badge-selector'
            title='Badge Selector'
            onCancel={ () => onCancel() }
            width={ {
                sm: '90vw',
                md: '80vw',
                xl: '1000px'
            } }
            onOk={ () => onOk() }
            okButtonProps={ { disabled: !selectedBadge } }
            open={ open }>

                { modalError && modalError }

                <div className='badge-finder-section'>

                    <span>Select one badge that will be awarded to the user when they complete this objective</span>

                    <Input.Search disabled={ badgeListLoading } onSearch={ onSearch } placeholder='Search for badge name or id' />
                    <div className='publish-switch'>
                        <Switch defaultChecked={ publishedOnly } disabled={ badgeListLoading || publishedOnly } onChange={ onSwitch } /> 
                        Published Only
                    </div>

                    <Flex wrap vertical={ false }>
                        { displayData }
                    </Flex>

                    { totalCount !== undefined && <Pagination disabled={ badgeListLoading } defaultCurrent={ 1 } total={ totalCount } onChange={ onPageChange } /> }
                </div>

                <div className='selected-badge-section'>
                    <h4>Selected badge:</h4>

                    { selectedBadge ? 
                        <BadgeItem badge={ selectedBadge } /> :
                        <span className='canceled'>No badge selected</span>
                    }
                </div>

        </Modal>

    </div>;

};

export const BadgeItem = ( { badge, hoverable, onClick, minimalInfo, style, extra } ) => {

    const image = useMemo( () => <Image src={ badge?.image } alt={ badge?.name ? `${ badge?.name.substring( 0, 5 ) }...` : 'badge' } width={ 200 } preview={ false } />, [ badge ] );

    return <Card 
        size='small' 
        style={ style }
        hoverable={ hoverable } 
        onClick={ () => onClick?.( badge ) } 
        extra={ extra }
        title={ badge?.name } 
        >
            { !badge ? <Spin /> :
                !minimalInfo ?
                    <Descriptions
                        layout='vertical'
                        items={ [
                            {
                                key: 'desc',
                                label: `Description (${ DEFAULT_LOCALE_CODE })`,
                                children: badge.description?.length > 0 ?
                                    <Typography.Paragraph style={ { marginTop: 8 } }>
                                        { badge.description }
                                    </Typography.Paragraph> :
                                    <span className='canceled'>No description</span>
                            }, ...( badge.createdAt ? [ {
                                key: 'createdAt',
                                label: 'Created At',
                                children: badge.createdAt.format( 'LLL' ),
                            } ] : [] ), {
                                key: 'image',
                                label: 'Image',
                                children: <Image src={ badge.image } width={ 200 } preview={ false } />
                            }, {
                                key: 'badgecount',
                                label: 'Users Awarded',
                                children: badge.personBadgeCount
                            }, {
                                key: 'repeatable',
                                label: 'Repeatable',
                                children: <ColoredTag.Repeatable isRepeatable={ badge.isRepeatable } />
                            }, {
                                key: 'published',
                                label: 'Published',
                                children: <ColoredTag.Publish isPublished={ badge.isPublished } />
                            }
                        ] }
                    /> : image

            }
    </Card>;
};